import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ProcurementContainer from '../../procurement/containers/ProcurementContainer';
import {
  BomItemWithRelations,
  Development,
  PurchaseOrderWithRelations,
  useGetBomItemsByProjectByProjectIdQuery,
  useGetBomQuery,
  useGetDevelopmentsQuery,
  useGetPurchaseOrdersQuery,
} from '../../../redux/vmsApi';
import { getCurrentDate } from '../../../utils/dateUtil';
import { formatCurrency } from '../../../utils/currencyUtil';

type MetricCardProps = {
  title: string;
  value: number;
  caption: string;
  accent: string;
  icon: React.ReactNode;
  quantity?: number;
  quantityLabel?: string;
  quantityUnit?: string;
  showQuantity?: boolean;
  currencyCode?: string;
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  caption,
  accent,
  icon,
  quantity,
  quantityLabel = 'Quantity',
  quantityUnit,
  showQuantity = false,
  currencyCode = 'USD',
}) => (
  <Card
    sx={{
      borderRadius: 3,
      height: '100%',
      background: `linear-gradient(145deg, ${accent}22 0%, #ffffff 80%)`,
      border: `1px solid ${accent}55`,
      boxShadow: `0 10px 24px ${accent}22`,
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: '#2f3e46', fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ color: accent }}>{icon}</Box>
      </Stack>

      <Typography variant="h5" sx={{ fontWeight: 800, color: accent, mt: 1.5 }}>
        {formatCurrency(value, currencyCode)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#415a77', mt: 0.5 }}>
        {caption}
      </Typography>

      {showQuantity ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1b263b', mt: 1.25 }}>
            {quantity?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? '0'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#415a77' }}>
            {quantityUnit ? `${quantityLabel} (${quantityUnit})` : quantityLabel}
          </Typography>
        </>
      ) : null}
    </CardContent>
  </Card>
);

const toDate = (value?: unknown): Date | null => {
  if (!value) return null;
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const inRange = (date: Date | null, fromDate: string, toDateValue: string): boolean => {
  if (!date) return false;
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDateValue}T23:59:59.999`);
  return date >= from && date <= to;
};

const normalizeText = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const BackOrderTrackingPage: React.FC = () => {
  const [fromDate, setFromDate] = useState('2025-01-01');
  const [toDateValue, setToDateValue] = useState(getCurrentDate());
  const [selectedDevelopmentId, setSelectedDevelopmentId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedBomItemId, setSelectedBomItemId] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const { data: developments = [], isLoading: developmentsLoading } = useGetDevelopmentsQuery();
  const { data: boms = [] } = useGetBomQuery({});
  const { data: projectBomItems = [] } = useGetBomItemsByProjectByProjectIdQuery(
    { projectId: selectedProjectId, limit: 5000, skip: 0 },
    { skip: !selectedProjectId },
  );

  const selectedDevelopment = useMemo(
    () => developments.find((d) => String(d.id) === selectedDevelopmentId),
    [developments, selectedDevelopmentId],
  );

  const availableProjects = selectedDevelopment?.projects ?? [];
  const projectBom = useMemo(
    () => boms.find((bom) => String(bom.project?.id) === selectedProjectId),
    [boms, selectedProjectId],
  );

  const selectedProjectName = availableProjects.find((project) => String(project.id) === selectedProjectId)?.name ?? '';
  const selectedDevelopmentName = selectedDevelopment?.name ?? '';

  const bomItemOptions = useMemo(
    () => [
      { id: 'all', label: 'All BOM Items' },
      ...projectBomItems.map((item) => ({
        id: String(item.id),
        label: `${item.itemCode} - ${item.itemDescription}`,
      })),
    ],
    [projectBomItems],
  );

  const { data: purchaseOrders = [] } = useGetPurchaseOrdersQuery(
    {
      development: selectedDevelopmentName,
      project: selectedProjectName,
      fromDate,
      toDate: toDateValue,
      limit: 5000,
      skip: 0,
      detailed: true,
    },
    { skip: !selectedDevelopmentId || !selectedProjectId },
  );

  const scopedBomItems = useMemo(() => {
    if (!selectedProjectId) return [] as BomItemWithRelations[];
    if (selectedBomItemId === 'all') return projectBomItems;
    return projectBomItems.filter((item) => String(item.id) === selectedBomItemId);
  }, [projectBomItems, selectedProjectId, selectedBomItemId]);

  const filteredPurchaseOrders = useMemo(() => {
    if (!selectedCurrency || selectedCurrency === 'All') {
      return purchaseOrders;
    }

    const expectedCurrency = selectedCurrency.toUpperCase();
    return purchaseOrders.filter((purchaseOrder) => normalizeText(purchaseOrder.quotation?.currency) === expectedCurrency.toLowerCase());
  }, [purchaseOrders, selectedCurrency]);

  const isSingleBomItemSelected = selectedBomItemId !== 'all' && scopedBomItems.length === 1;
  const selectedBomItem = useMemo(
    () => (isSingleBomItemSelected ? scopedBomItems[0] : undefined),
    [isSingleBomItemSelected, scopedBomItems],
  );

  const scopedItemCodes = useMemo(
    () => new Set(scopedBomItems.map((item) => normalizeText(item.itemCode)).filter(Boolean)),
    [scopedBomItems],
  );

  const transferItemPassesBomFilter = (itemCode: string): boolean => {
    const code = normalizeText(itemCode);
    if (!scopedItemCodes.size) return true;
    return scopedItemCodes.has(code);
  };

  const baselineBudgetValue = useMemo(
    () => scopedBomItems.reduce((sum, item) => sum + Number(item.itemInitialValue ?? 0), 0),
    [scopedBomItems],
  );

  const baselineBudgetQty = useMemo(
    () => (isSingleBomItemSelected
      ? scopedBomItems.reduce((sum, item) => sum + Number(item.initialQuantity ?? 0), 0)
      : 0),
    [scopedBomItems, isSingleBomItemSelected],
  );

  const orderedViaBudgetValue = useMemo(
    () => scopedBomItems.reduce((sum, item) => sum + Number(item.procuredValue ?? 0), 0),
    [scopedBomItems],
  );

  const orderedViaBudgetQty = useMemo(
    () => (isSingleBomItemSelected
      ? scopedBomItems.reduce((sum, item) => sum + Number(item.procuredQuantity ?? 0), 0)
      : 0),
    [scopedBomItems, isSingleBomItemSelected],
  );

  const orderedSummary = useMemo(() => {
    let value = 0;
    let qty = 0;
    let unit = '';

    filteredPurchaseOrders.forEach((purchaseOrder) => {
      const date = toDate(purchaseOrder.date);
      if (!inRange(date, fromDate, toDateValue)) return;

      if (!isSingleBomItemSelected) {
        value += Number(purchaseOrder.quotation?.grandTotal ?? 0) || 0;
        return;
      }

      (purchaseOrder.quotation?.quotationItems ?? []).forEach((item) => {
        if (!transferItemPassesBomFilter(item.itemCode)) return;
        value += Number(item.totalCostPerItem ?? 0) || 0;
        qty += Number(item.quantity?.quantityValue ?? 0) || 0;
        if (!unit && item.quantity?.quantityUnit) {
          unit = item.quantity.quantityUnit;
        }
      });
    });

    return { value, qty, unit };
  }, [purchaseOrders, fromDate, toDateValue, isSingleBomItemSelected, scopedItemCodes]);

  const orderedValue = orderedSummary.value;
  const orderedQty = orderedSummary.qty;
  const orderedUnit = orderedSummary.unit || selectedBomItem?.itemUnit || '';

  const deliveredSummary = useMemo(() => {
    let value = 0;
    let qty = 0;
    let unit = '';

    filteredPurchaseOrders.forEach((purchaseOrder) => {
      const date = toDate(purchaseOrder.date);
      if (!inRange(date, fromDate, toDateValue)) return;

      if (!isSingleBomItemSelected) {
        value += Number(purchaseOrder.fullFilledValue ?? 0) || 0;
        return;
      }

      (purchaseOrder.itemsReceived ?? []).forEach((item) => {
        if (!transferItemPassesBomFilter(item.itemCode)) return;
        value += Number(item.totalCostPerItem ?? 0) || 0;
        qty += Number(item.quantity?.quantityValue ?? 0) || 0;
        if (!unit && item.quantity?.quantityUnit) {
          unit = item.quantity.quantityUnit;
        }
      });
    });

    return { value, qty, unit };
  }, [purchaseOrders, fromDate, toDateValue, isSingleBomItemSelected, scopedItemCodes]);

  const deliveredValue = deliveredSummary.value;
  const deliveredQty = deliveredSummary.qty;
  const deliveredUnit = deliveredSummary.unit || orderedUnit;
  const undeliveredValue = Math.max(orderedValue - deliveredValue, 0);
  const undeliveredQty = Math.max(orderedQty - deliveredQty, 0);
  const costToCompletionValue = Math.max(baselineBudgetValue - orderedViaBudgetValue, 0);
  const costToCompletionQty = Math.max(baselineBudgetQty - orderedViaBudgetQty, 0);
  const quantityUnit = orderedUnit || deliveredUnit || selectedBomItem?.itemUnit || '';

  const chartData = useMemo(() => {
    const rows = filteredPurchaseOrders.map((purchaseOrder, index) => {
      const ordered = Number(purchaseOrder.quotation?.grandTotal ?? 0) || 0;
      const delivery = Number(purchaseOrder.fullFilledValue ?? 0) || 0;

      return {
        name: purchaseOrder.purchaseOrderNumber ?? purchaseOrder.quotation?.quotationNumber ?? `PO ${index + 1}`,
        ordered,
        delivery,
        outstanding: Math.max(ordered - delivery, 0),
      };
    });

    return rows.slice(0, 10);
  }, [filteredPurchaseOrders]);

  const statusReady = Boolean(selectedDevelopmentId && selectedProjectId);

  const handleDevelopmentChange = (event: SelectChangeEvent<string>) => {
    setSelectedDevelopmentId(event.target.value);
    setSelectedProjectId('');
    setSelectedBomItemId('all');
  };

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    setSelectedProjectId(event.target.value);
    setSelectedBomItemId('all');
  };

  return (
    <ProcurementContainer>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #e0f2fe 0%, #f3e8ff 100%)',
            border: '1px solid #d0d7de',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                BackOrderTracking
              </Typography>
              <Typography variant="body1" sx={{ color: '#334155', mt: 1 }}>
                Track ordered, delivered, and undelivered material values for the selected project scope.
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
              · Ordered · Delivered · Undelivered
            </Typography>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: '#f8fafc', boxShadow: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="To Date"
                type="date"
                value={toDateValue}
                onChange={(event) => setToDateValue(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={developmentsLoading}>
                <InputLabel>Development</InputLabel>
                <Select value={selectedDevelopmentId} label="Development" onChange={handleDevelopmentChange}>
                  {developments.map((development: Development) => (
                    <MenuItem key={String(development.id)} value={String(development.id)}>
                      {development.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedDevelopmentId}>
                <InputLabel>Project</InputLabel>
                <Select value={selectedProjectId} label="Project" onChange={handleProjectChange}>
                  {availableProjects.map((project) => (
                    <MenuItem key={String(project.id)} value={String(project.id)}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={bomItemOptions}
                value={bomItemOptions.find((opt) => opt.id === selectedBomItemId) ?? bomItemOptions[0] ?? null}
                onChange={(_event, option) => setSelectedBomItemId(option?.id ?? 'all')}
                disabled={!selectedProjectId}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="BOM Item" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select value={selectedCurrency} label="Currency" onChange={(event) => setSelectedCurrency(event.target.value)}>
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!statusReady && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Select Development and Project to load the back-order tracking view.
            </Alert>
          )}

          {statusReady && (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} mt={2}>
              <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>
                Scope: {selectedProjectName || 'All Project Items'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                BOM: {projectBom?.title ?? '-'}
              </Typography>
            </Stack>
          )}
        </Paper>

        {statusReady && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Budget"
                  value={baselineBudgetValue}
                  quantity={baselineBudgetQty}
                  quantityLabel="Baseline budget quantity"
                  quantityUnit={selectedBomItem?.itemUnit || quantityUnit}
                  showQuantity={isSingleBomItemSelected}
                  caption="Initial budget baseline for the selected scope"
                  accent="#0f766e"
                  icon={<AccountBalanceWalletIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Ordered"
                  value={orderedValue}
                  quantity={orderedQty}
                  quantityLabel="Ordered quantity"
                  quantityUnit={quantityUnit}
                  showQuantity={isSingleBomItemSelected}
                  caption="Purchase-order quotation totals for the selected scope"
                  accent="#7c3aed"
                  icon={<ShoppingCartIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Ordered Via Budget"
                  value={orderedViaBudgetValue}
                  quantity={orderedViaBudgetQty}
                  quantityLabel="Budget-ordered quantity"
                  quantityUnit={selectedBomItem?.itemUnit || quantityUnit}
                  showQuantity={isSingleBomItemSelected}
                  caption="Amount ordered through the budgeting workflow"
                  accent="#6366f1"
                  icon={<AccountBalanceWalletIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Cost To Completion"
                  value={costToCompletionValue}
                  quantity={costToCompletionQty}
                  quantityLabel="Cost-to-completion quantity"
                  quantityUnit={selectedBomItem?.itemUnit || quantityUnit}
                  showQuantity={isSingleBomItemSelected}
                  caption="Baseline budget minus ordered-via-budget"
                  accent="#f59e0b"
                  icon={<TrendingUpIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Delivered"
                  value={deliveredValue}
                  quantity={deliveredQty}
                  quantityLabel="Delivered quantity"
                  quantityUnit={quantityUnit}
                  showQuantity={isSingleBomItemSelected}
                  caption="Value received within the selected date range"
                  accent="#0d9488"
                  icon={<LocalShippingIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Undelivered"
                  value={undeliveredValue}
                  quantity={undeliveredQty}
                  quantityLabel="Undelivered quantity"
                  quantityUnit={quantityUnit}
                  showQuantity={isSingleBomItemSelected}
                  caption="Outstanding value still pending delivery"
                  accent="#dc2626"
                  icon={<Inventory2Icon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
            </Grid>

            <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
                Delivery Snapshot
              </Typography>
              <Box sx={{ width: '100%', height: 340 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 40, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tickMargin={8} />
                    <YAxis
                      tickMargin={8}
                      width={90}
                      tickFormatter={(value) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)}
                      domain={[0, 'dataMax + 1000']}
                    />
                    <Tooltip formatter={(value: number) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)} />
                    <Legend />
                    <Line type="monotone" dataKey="ordered" stroke="#7c3aed" strokeWidth={2.4} name="Ordered" />
                    <Line type="monotone" dataKey="delivery" stroke="#0d9488" strokeWidth={2.4} name="Delivered" />
                    <Line type="monotone" dataKey="outstanding" stroke="#dc2626" strokeWidth={2.4} name="Undelivered" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </>
        )}
      </Stack>
    </ProcurementContainer>
  );
};

export default BackOrderTrackingPage;
